/**
 * Product page behaviour (loaded by sections/main-product.liquid):
 * - <product-form>: variant buttons, made-to-order notice, price refresh, add to cart
 * - <product-gallery>: thumbnails, jump to a colour's photo, full-screen zoom
 * - <size-guide>: Elas/Eles tabs and the drawing ↔ table highlight
 * - <product-siblings>: "Outras opções", products that share a `modelo-*` tag
 * - <product-bundle>: "Compre junto", products that share a `cj-*` tag
 * The gallery's video plays through window.Miscela.video (assets/video.js, loaded before this file).
 */
(function () {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const normalize = (text) =>
    (text || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/\(.*?\)/g, '')
      .replace(/\s+/g, ' ')
      .trim();

  const parseHTML = async (url) => {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return new DOMParser().parseFromString(await response.text(), 'text/html');
  };

  const searchSectionUrl = (query, sectionId) => {
    const url = new URL(`${root}search`, window.location.origin);
    url.searchParams.set('type', 'product');
    url.searchParams.set('q', query);
    url.searchParams.set('section_id', sectionId);
    return url;
  };

  // Options whose values have their own photos: choosing one shows it (the Bolsa Lateral prints since 2026-10-04).
  const MEDIA_OPTIONS = new Set(['cor', 'cores', 'estampa', 'estampas']);

  const money = (cents) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(cents / 100).replace(/\u00a0/g, ' ');

  // A percentage discount on one cart line, in cents. Shopify drops the fraction of a cent (checked on
  // 2026-10-04: 5% of R$ 139,90 is R$ 6,99; 10% of R$ 209,99 is R$ 20,99), so the page shows the cart's total.
  const lineDiscount = (cents, percent) => Math.floor((cents * percent) / 100);

  // An add whose answer was lost (the connection dropped, an answer that cannot be read) may still have gone in:
  // "Tente de novo" led to adding it twice (until 2026-10-08).
  const LOST_ANSWER = 'Não foi possível confirmar se a peça entrou no carrinho. Confira o carrinho antes de tentar de novo.';

  // An add to the cart, through the cart's queue when the page has one (assets/cart.js): it leaves after the changes
  // before it, and checkout waits for it; a failed one makes it read the cart again. `send` rejects with the message
  // to show.
  const addToCart = (send) => (window.Miscela && window.Miscela.cartRequest ? window.Miscela.cartRequest(send) : send());

  // What is left of a variant's ready stock once the pieces of it already in the cart without the mark take theirs
  // (assets/cart.js reads the cart on screen; without it, none).
  const readyLeft = (variant) =>
    variant.stock - (window.Miscela && window.Miscela.unmarkedInCart ? window.Miscela.unmarkedInCart(variant.id) : 0);

  // A cart line for one piece of a variant; a made-to-order one (sold on backorder, no ready stock left) carries the
  // "Envio" property (layout/theme.liquid).
  const lineItem = (variant) => {
    const line = { id: variant.id, quantity: 1 };
    const property = window.Miscela && window.Miscela.madeToOrder;
    if (variant.backorder && readyLeft(variant) < 1 && property) line.properties = { [property.key]: property.value };
    return line;
  };

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-product-form]');
      this.variants = JSON.parse(this.querySelector('[data-product-json]').textContent).variants;
      this.idInput = this.form.querySelector('[data-variant-id]');
      // Fieldsets follow the display order (the fit comes first); `data-option-index` is the
      // option's position in the product, which is what variant.options uses.
      this.fieldsets = [...this.form.querySelectorAll('.product-option')];
      this.optionCount = this.variants[0] ? this.variants[0].options.length : 0;
      this.fitFieldset = this.form.querySelector('[data-fit-option]');
      this.submitButton = this.form.querySelector('[data-add-button]');
      this.errorElement = this.form.querySelector('[data-form-error]');
      this.notice = this.form.querySelector('[data-made-to-order]');
      this.stockLine = this.form.querySelector('[data-stock]');
      this.madeToOrderProperty = this.form.querySelector('[data-made-to-order-property]');
      this.quantityInput = this.form.querySelector('input[name="quantity"]');

      this.form.addEventListener('change', (event) => {
        if (event.target.name && event.target.name.startsWith('option-')) this.onOptionChange(event.target);
      });
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
      this.form.querySelectorAll('[data-quantity]').forEach((button) =>
        button.addEventListener('click', () => this.stepQuantity(Number(button.dataset.quantity)))
      );
      this.quantityInput?.addEventListener('input', () => this.refreshMadeToOrder());
      // The cart changed (an add, a change in the drawer, an app): the ready stock left may have too.
      document.addEventListener('cart:rendered', () => this.refreshMadeToOrder());
      this.update({ initial: true });
    }

    get selection() {
      const selection = new Array(this.optionCount).fill(null);
      this.fieldsets.forEach((fieldset) => {
        selection[Number(fieldset.dataset.optionIndex)] = fieldset.querySelector('input:checked')?.value ?? null;
      });
      return selection;
    }

    matching(selection) {
      return this.variants.filter((variant) =>
        selection.every((value, index) => value === null || variant.options[index] === value)
      );
    }

    onOptionChange(input) {
      const fieldset = input.closest('.product-option');
      const index = Number(fieldset.dataset.optionIndex);
      const picked = input.value;

      // A value that does not combine with the other choices wins: keep as many of them as one variant with it allows
      // (an available one first) and clear the rest. Checking pairs alone is not enough with three options: a value
      // can pair with each of the others and still make no variant with both (four shirts on 2026-10-07).
      const selection = this.selection;
      if (this.matching(selection).length === 0) {
        const agreeing = (variant) => selection.filter((value, position) => value !== null && variant.options[position] === value).length;
        const best = this.variants
          .filter((variant) => variant.options[index] === picked)
          .reduce((kept, variant) => {
            if (!kept) return variant;
            const score = agreeing(variant) * 2 + (variant.available ? 1 : 0);
            return score > agreeing(kept) * 2 + (kept.available ? 1 : 0) ? variant : kept;
          }, null);
        this.fieldsets.forEach((other) => {
          const otherIndex = Number(other.dataset.optionIndex);
          const checked = other.querySelector('input:checked');
          if (otherIndex !== index && checked && (!best || best.options[otherIndex] !== checked.value)) checked.checked = false;
        });
      }

      fieldset.classList.remove('is-missing');
      if (!this.errorElement.hidden) this.showMissing();

      if (MEDIA_OPTIONS.has(fieldset.dataset.optionName)) this.showMediaFor(this.selection);
      this.update();
    }

    update({ initial = false } = {}) {
      const selection = this.selection;

      this.fieldsets.forEach((fieldset) => {
        const index = Number(fieldset.dataset.optionIndex);
        fieldset.querySelector('[data-option-current]').textContent = selection[index] ?? '';
        fieldset.querySelectorAll('input').forEach((input) => {
          const trial = selection.map((value, position) => (position === index ? input.value : value));
          const possible = this.matching(trial).some((variant) => variant.available);
          input.closest('.option-button').classList.toggle('is-unavailable', !possible);
        });
      });

      // A product with no options shows no buttons, yet Shopify gives it one ("Default Title"): it buys its only variant.
      const complete = selection.every((value) => value !== null);
      const variant = !this.fieldsets.length ? this.variants[0] || null : complete ? this.matching(selection)[0] || null : null;
      this.variant = variant;
      this.idInput.value = variant ? variant.id : '';
      this.refreshMadeToOrder();

      // With no variant chosen yet the button follows the product: nothing left to sell keeps "Esgotado".
      const soldOut = variant ? !variant.available : !this.variants.some((candidate) => candidate.available);
      this.submitButton.disabled = soldOut;
      this.submitButton.textContent = soldOut ? 'Esgotado' : 'Comprar';

      if (initial) return;
      const url = new URL(window.location.href);
      if (variant) url.searchParams.set('variant', variant.id);
      else url.searchParams.delete('variant');
      window.history.replaceState(window.history.state, '', url);
      this.renderVariant(variant ? variant.id : null);
    }

    /**
     * A variant sold on backorder is made to order when the quantity asked passes its ready stock left (none left, or
     * fewer than asked; the pieces of it already in the cart without the mark take theirs first): the notice shows and
     * the "Envio" line item property goes along (enabled inputs only are sent). Otherwise the stock line says how that
     * ready stock stands: "Em estoque" with more than one piece left (or any, on backorder), "Somente 1 disponível" with
     * the last one of a piece that sells only its stock, nothing with none left.
     */
    refreshMadeToOrder() {
      const quantity = Math.max(1, Number(this.quantityInput?.value) || 1);
      const variant = this.variant;
      const left = variant ? readyLeft(variant) : 0;
      const madeToOrder = Boolean(variant && variant.backorder && quantity > left);
      this.notice.hidden = !madeToOrder;
      if (this.madeToOrderProperty) this.madeToOrderProperty.disabled = !madeToOrder;

      if (!this.stockLine) return;
      let text = '';
      if (variant && variant.tracked && !madeToOrder) {
        if (left > 1 || (left === 1 && variant.backorder)) text = 'Em estoque';
        else if (left === 1) text = 'Somente 1 disponível';
      }
      this.stockLine.textContent = text;
      this.stockLine.classList.toggle('is-last', text === 'Somente 1 disponível');
      this.stockLine.hidden = !text;
    }

    /** Refresh the price block with the server-rendered version (same Liquid as the first load). */
    async renderVariant(variantId) {
      const token = Symbol('render');
      this.renderToken = token;
      const url = new URL(this.dataset.productUrl, window.location.origin);
      if (variantId) url.searchParams.set('variant', variantId);
      url.searchParams.set('section_id', this.dataset.sectionId);
      try {
        const html = await parseHTML(url);
        // Only the latest selection may update the page; an older, slower response is dropped.
        if (token !== this.renderToken) return;
        const section = this.closest('.shopify-section') || document;
        section.querySelectorAll('[data-product-update]').forEach((element) => {
          const fresh = html.querySelector(`[data-product-update="${element.dataset.productUpdate}"]`);
          if (fresh) element.replaceWith(fresh);
        });
      } catch (error) {
        // Keep the current price; the cart shows the right one anyway.
      }
    }

    showMediaFor(selection) {
      const withMedia = this.matching(selection).find((variant) => variant.media);
      if (!withMedia) return;
      const gallery = this.closest('.shopify-section')?.querySelector('product-gallery');
      gallery?.goToMedia(withMedia.media);
    }

    stepQuantity(step) {
      const next = Math.max(1, (Number(this.quantityInput.value) || 1) + step);
      this.quantityInput.value = next;
      this.refreshMadeToOrder();
    }

    showError(message) {
      this.errorElement.textContent = message;
      this.errorElement.hidden = !message;
    }

    /** Lists the options still to choose; returns them (empty when all are chosen). */
    showMissing() {
      let missing = this.fieldsets.filter((fieldset) => !fieldset.querySelector('input:checked'));
      // While the fit is not chosen the other options are hidden: ask for the fit only.
      if (this.fitFieldset && missing.includes(this.fitFieldset)) missing = [this.fitFieldset];
      this.fieldsets.forEach((fieldset) => fieldset.classList.toggle('is-missing', missing.includes(fieldset)));
      if (!missing.length) {
        this.showError('');
        return missing;
      }
      if (missing.length === 1 && missing[0] === this.fitFieldset) {
        this.showError('Escolha a modelagem.');
        return missing;
      }
      const names = missing.map((fieldset) => fieldset.dataset.optionLabel.toLowerCase());
      const list = names.length > 1 ? `${names.slice(0, -1).join(', ')} e ${names.at(-1)}` : names[0];
      this.showError(`Escolha ${list}.`);
      return missing;
    }

    async onSubmit(event) {
      event.preventDefault();
      // An add still on its way: another submit (a second tap, Enter from a field) would add the piece twice.
      if (this.adding) return;
      const missing = this.showMissing();
      if (missing.length) {
        missing[0].querySelector('input').focus();
        return;
      }
      if (!this.variant) {
        this.showError('Essa combinação não está disponível.');
        return;
      }
      this.showError('');
      // A quantity typed out of range (0, negative, a fraction, empty) would be refused by the cart: a whole number of
      // at least 1, shown in the field (the form is novalidate, so the browser does not stop it).
      if (this.quantityInput) {
        const typed = Math.floor(Number(this.quantityInput.value));
        this.quantityInput.value = typed >= 1 ? typed : 1;
        this.refreshMadeToOrder();
      }

      this.adding = true;
      this.submitButton.setAttribute('aria-busy', 'true');
      const own = (window.Miscela && window.Miscela.cartHeaders) || {};
      // The piece and quantity of the tap: the options and the quantity stay editable while it waits in the queue, and
      // what goes is what was tapped (until 2026-10-09 the form was read at its turn).
      const variant = this.variant;
      const quantity = Math.max(1, Number(this.quantityInput?.value) || 1);
      const tapped = new FormData(this.form);
      for (const key of [...tapped.keys()]) if (key.startsWith('option-')) tapped.delete(key);
      const mark = this.madeToOrderProperty;
      const send = async () => {
        // Only the "Envio" mark is decided now, at its turn in the queue: a change before it (a "+" in the cart for this
        // piece) may have used up the ready stock, and the mark counts the cart as it left it (until 2026-10-08 it was
        // decided at the tap, and the piece joined an unmarked line).
        this.refreshMadeToOrder();
        const body = new FormData();
        for (const [key, value] of tapped) body.append(key, value);
        if (mark) {
          body.delete(mark.name);
          if (variant.backorder && quantity > readyLeft(variant)) body.append(mark.name, mark.value);
        }
        const sections = (window.Miscela && window.Miscela.cartSections && window.Miscela.cartSections()) || [];
        if (sections.length) {
          body.append('sections', sections.join(','));
          body.append('sections_url', window.location.pathname);
        }
        // Sequence taken when the request leaves, so the cart drawer never shows an older cart
        // over a newer one (see assets/cart.js).
        const seq = window.Miscela && window.Miscela.nextCartSeq ? window.Miscela.nextCartSeq() : 0;
        let response;
        let data;
        try {
          response = await fetch(`${this.dataset.cartAddUrl}.js`, {
            method: 'POST',
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest', ...own },
            body,
          });
          data = await response.json();
        } catch (error) {
          // No answer, or one that cannot be read: the piece may have gone in all the same (the cart is read again).
          throw new Error(LOST_ANSWER);
        }
        if (!response.ok) throw new Error(data.description || data.message || 'Não foi possível adicionar ao carrinho.');
        const handled = !document.dispatchEvent(
          new CustomEvent('cart:added', { cancelable: true, detail: { item: data, sections: data.sections, seq } })
        );
        // Without a cart drawer to take over, go to the cart page.
        if (!handled) window.location.href = this.dataset.cartUrl;
      };
      try {
        await addToCart(send);
      } catch (error) {
        this.showError(error.message);
      } finally {
        this.adding = false;
        this.submitButton.removeAttribute('aria-busy');
      }
    }
  }

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-main] [data-carousel-track]');
      if (!this.track) return;
      this.thumbs = [...this.querySelectorAll('[data-thumb-index]')];
      this.thumbList = this.querySelector('.product-gallery__thumbs');
      this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.thumbIndex))));
      // A link to a variant opens on its photo (data-start-index, from the Liquid): placed at once, no animation.
      const start = Number(this.dataset.startIndex) || 0;
      if (start) {
        this.track.scrollLeft = start * this.track.clientWidth;
        this.markThumb(start);
      }
      this.track.addEventListener(
        'scroll',
        () => {
          if (this.frame) return;
          this.frame = window.requestAnimationFrame(() => {
            this.frame = null;
            this.markThumb(Math.round(this.track.scrollLeft / this.track.clientWidth));
          });
        },
        { passive: true }
      );

      this.dialog = document.getElementById(this.dataset.zoomDialog);
      if (this.dialog) {
        this.querySelectorAll('[data-zoom-index]').forEach((button) =>
          button.addEventListener('click', () => this.openZoom(Number(button.dataset.zoomIndex)))
        );
        this.dialog.querySelector('[data-zoom-close]').addEventListener('click', () => this.dialog.close());
      }

      this.videoSlide = this.querySelector('[data-gallery-video-slide]');
      if (this.videoSlide) this.setupVideo();
    }

    // The product's video (2nd item): its file loads only when its slide is on screen, where it plays muted with
    // controls to turn the sound on; it pauses when the slide leaves. Reduced motion: it waits for a tap.
    setupVideo() {
      const video = this.videoSlide.querySelector('[data-gallery-video]');
      const { play, pause } = window.Miscela.video;
      const start = (withSound) => {
        if (withSound) video.muted = false;
        video.controls = true;
        play(video);
      };
      video.addEventListener('playing', () => this.videoSlide.classList.add('is-playing'));
      this.videoSlide.querySelector('[data-gallery-video-play]').addEventListener('click', () => start(true));
      if (!('IntersectionObserver' in window)) return;
      new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              if (!reducedMotion.matches) start(false);
            } else if (video.getAttribute('src') || video.dataset.playing) {
              // Also while its file is still being chosen, so it does not start once the playlist arrives.
              pause(video);
            }
          });
        },
        { threshold: 0.6 }
      ).observe(this.videoSlide);
    }

    goTo(index) {
      this.track.scrollTo({
        left: index * this.track.clientWidth,
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
      });
      this.markThumb(index);
    }

    goToMedia(mediaId) {
      const index = [...this.track.children].findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (index >= 0) this.goTo(index);
    }

    markThumb(index) {
      // "Clique para ampliar" is not shown on the video.
      this.classList.toggle('is-video', Boolean(this.track.children[index]?.hasAttribute('data-gallery-video-slide')));
      this.thumbs.forEach((thumb, position) => thumb.setAttribute('aria-current', String(position === index)));
      const thumb = this.thumbs[index];
      if (!thumb || !this.thumbList) return;
      // Scroll the thumbnail row only (scrollIntoView could also scroll the page).
      const left = thumb.parentElement.offsetLeft - (this.thumbList.clientWidth - thumb.clientWidth) / 2;
      this.thumbList.scrollTo({ left, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    }

    openZoom(index) {
      this.dialog.showModal();
      this.dialog.querySelector(`[data-zoom-item="${index}"]`)?.scrollIntoView({ block: 'start' });
    }
  }

  class SizeGuide extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.content = this.querySelector('[data-size-guide-content]');
      this.tabList = this.querySelector('[data-size-guide-tabs]');
      this.prepare();

      this.onDocumentClick = (event) => {
        const opener = event.target.closest('[data-size-guide-open]');
        if (opener && opener.getAttribute('aria-controls') === this.dialog.id) this.open();
      };
      document.addEventListener('click', this.onDocumentClick);
      this.querySelector('[data-size-guide-close]').addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });
      this.tabList.querySelectorAll('[data-fit]').forEach((tab) =>
        tab.addEventListener('click', () => this.show(tab.dataset.fit))
      );
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
    }

    prepare() {
      this.tables = [...this.content.querySelectorAll('table')].map((table) => {
        const head = normalize(table.querySelector('th, td')?.textContent);
        let fit = 'all';
        if (/\beles\b/.test(head)) fit = 'eles';
        else if (/\belas\b/.test(head)) fit = 'elas';
        const rows = [...table.querySelectorAll('tbody tr')];
        rows.forEach((row) => {
          const label = normalize(row.cells[0]?.textContent);
          if (!label) return;
          row.dataset.measureRow = label;
          row.tabIndex = 0;
          row.addEventListener('mouseenter', () => this.highlight(label));
          row.addEventListener('focus', () => this.highlight(label));
          row.addEventListener('mouseleave', () => this.highlight(null));
          row.addEventListener('blur', () => this.highlight(null));
        });
        return { table, fit, labels: rows.map((row) => row.dataset.measureRow).filter(Boolean) };
      });

      const fits = new Set(this.tables.map((item) => item.fit));
      // A generic table next to an "Eles" one is the women's table (e.g. "Modelagem (cm)").
      if (fits.has('eles') && fits.has('all') && !fits.has('elas')) {
        this.tables.forEach((item) => {
          if (item.fit === 'all') item.fit = 'elas';
        });
        fits.add('elas');
      }
      this.tables.forEach((item) => (item.table.dataset.fitTable = item.fit));
      this.hasTabs = fits.has('elas') && fits.has('eles');
      this.tabList.hidden = !this.hasTabs;
      this.show(fits.has('eles') && !fits.has('elas') ? 'eles' : 'elas');
    }

    show(fit) {
      this.fit = fit;
      this.tabList.querySelectorAll('[data-fit]').forEach((tab) => tab.setAttribute('aria-selected', String(tab.dataset.fit === fit)));

      const visible = this.tables.filter((item) => !this.hasTabs || item.fit === fit || item.fit === 'all');
      this.tables.forEach((item) => (item.table.hidden = !visible.includes(item)));

      const drawings = [...this.querySelectorAll('[data-drawing]')];
      const generic = drawings.find((drawing) => drawing.dataset.drawing === 'all');
      this.drawing = generic || drawings.find((drawing) => drawing.dataset.drawing === fit) || drawings[0];
      drawings.forEach((drawing) => (drawing.hidden = drawing !== this.drawing));

      const labels = new Set(visible.flatMap((item) => item.labels));
      // A piece's own measurements (jumpsuits, the regata) come with no drawing.
      this.drawing?.querySelectorAll('[data-measure]').forEach((measure) => {
        // SVG elements have no `hidden` property: set the attribute.
        measure.toggleAttribute('hidden', !measure.dataset.measure.split('|').some((key) => labels.has(key)));
      });
      this.highlight(null);
    }

    highlight(label) {
      const svg = this.drawing?.querySelector('svg');
      if (!svg) return;
      svg.classList.toggle('has-active', Boolean(label));
      svg.querySelectorAll('[data-measure]').forEach((measure) =>
        measure.classList.toggle('is-active', Boolean(label) && measure.dataset.measure.split('|').includes(label))
      );
      this.content
        .querySelectorAll('[data-measure-row]')
        .forEach((row) => row.classList.toggle('is-active', row.dataset.measureRow === label));
    }

    open() {
      // Open on the fit the customer already picked.
      const fit = document.querySelector('.product-option[data-option-name="modelagem"] input:checked');
      if (this.hasTabs && fit) this.show(normalize(fit.value) === 'eles' ? 'eles' : 'elas');
      this.dialog.showModal();
    }
  }

  class ProductSiblings extends HTMLElement {
    async connectedCallback() {
      try {
        const html = await parseHTML(searchSectionUrl(this.dataset.query, 'product-siblings'));
        const items = [...html.querySelectorAll('[data-sibling]')];
        if (items.length < 2) return;
        const current = items.find((item) => item.dataset.productId === this.dataset.current);
        if (current) {
          current.setAttribute('aria-current', 'page');
          current.removeAttribute('href');
          items.splice(items.indexOf(current), 1);
          items.unshift(current);
        }
        const list = document.createElement('div');
        list.className = 'product-siblings__list';
        list.append(...items);
        this.querySelector('[data-siblings-list]').replaceWith(list);
        this.hidden = false;
      } catch (error) {
        // Optional block: stays hidden.
      }
    }
  }

  class ProductBundle extends HTMLElement {
    async connectedCallback() {
      try {
        const html = await parseHTML(searchSectionUrl(this.dataset.query, 'product-bundle-items'));
        const items = [...html.querySelectorAll('[data-bundle-item]')];
        // The pair is offered with the product being viewed: when the group's search leaves it out, nothing is shown.
        if (items.length < 2 || !items.some((item) => item.dataset.productId === this.dataset.current)) return;
        // The product being viewed comes first.
        items.sort((a, b) => (b.dataset.productId === this.dataset.current) - (a.dataset.productId === this.dataset.current));
        this.items = items.slice(0, Number(this.dataset.max || 2));
        const list = this.querySelector('[data-bundle-list]');
        this.items.forEach((item, index) => {
          if (index > 0) {
            const plus = document.createElement('span');
            plus.className = 'product-bundle__plus';
            plus.setAttribute('aria-hidden', 'true');
            plus.textContent = '+';
            list.append(plus);
          }
          list.append(item);
          this.refreshChoices(item);
          item.addEventListener('change', () => {
            this.refreshChoices(item);
            this.update();
          });
        });
        this.button = this.querySelector('[data-bundle-add]');
        this.button.addEventListener('click', () => this.add());
        this.update();
        this.hidden = false;
      } catch (error) {
        // Optional block: stays hidden.
      }
    }

    /** Disable the values that do not make an available variant with the ones already chosen (product page rule). */
    refreshChoices(item) {
      const { variants } = JSON.parse(item.querySelector('[data-bundle-json]').textContent);
      const selects = [...item.querySelectorAll('select')];
      const chosen = selects.map((select) => select.value || null);
      selects.forEach((select, index) => {
        [...select.options].forEach((option) => {
          if (!option.value) return;
          const trial = chosen.map((value, position) => (position === index ? option.value : value));
          option.disabled = !variants.some(
            (variant) => variant.available && trial.every((value, position) => value === null || variant.options[position] === value)
          );
        });
      });
    }

    /** The chosen variant; a complete choice that does not exist or is sold out counts as none (`unavailable`). */
    variantOf(item) {
      const data = JSON.parse(item.querySelector('[data-bundle-json]').textContent);
      const selects = [...item.querySelectorAll('select')];
      const selection = selects.map((select) => select.value || null);
      if (selection.some((value) => value === null)) return { data, variant: null };
      const variant = data.variants.find((candidate) => selection.every((value, index) => candidate.options[index] === value));
      if (!variant || !variant.available) return { data, variant: null, unavailable: true };
      return { data, variant };
    }

    update() {
      // The group's automatic discount (section setting), taken line by line as the cart does.
      const percent = Number(this.dataset.discount) || 0;
      let total = 0;
      let compareTotal = 0;
      let discount = 0;
      this.items.forEach((item) => {
        const { data, variant } = this.variantOf(item);
        // Before a choice, as the cards (snippets/price.liquid): the lowest price among the variants on sale (all of
        // them when none is), and the struck price of a variant sold at it — data.price and data.compare_at_price are
        // each the lowest of any variant, sold out or another one.
        const onSale = data.variants.filter((candidate) => candidate.available);
        const pool = onSale.length ? onSale : data.variants;
        const price = variant ? variant.price : Math.min(...pool.map((candidate) => candidate.price));
        total += price;
        discount += lineDiscount(price, percent);
        const compareAt = variant
          ? variant.compare_at_price || 0
          : Math.max(0, ...pool.filter((candidate) => candidate.price === price).map((candidate) => candidate.compare_at_price || 0));
        compareTotal += Math.max(compareAt, price);
      });
      const pay = total - discount;
      this.querySelector('[data-bundle-count]').textContent = this.items.length;
      this.querySelector('[data-bundle-total]').textContent = money(pay);
      const compare = this.querySelector('[data-bundle-compare]');
      compare.textContent = compareTotal > pay ? money(compareTotal) : '';
      compare.hidden = compareTotal <= pay;
      const note = this.querySelector('[data-bundle-note]');
      if (note) {
        note.textContent = percent > 0 ? `Com ${percent}% de desconto, aplicado no carrinho.` : '';
        note.hidden = percent <= 0;
      }
    }

    async add() {
      // The pair is still being added: another Enter or space on the button would add it twice.
      if (this.adding) return;
      const error = this.querySelector('[data-bundle-error]');
      const chosen = this.items.map((item) => this.variantOf(item));
      const missing = chosen.findIndex((entry) => !entry.variant);
      if (missing >= 0) {
        error.textContent = chosen[missing].unavailable
          ? 'Essa combinação não está disponível.'
          : `Escolha as opções de ${chosen[missing].data.title}.`;
        error.hidden = false;
        this.items[missing].querySelector('select')?.focus();
        return;
      }
      error.hidden = true;
      const sections = (window.Miscela && window.Miscela.cartSections && window.Miscela.cartSections()) || [];
      this.adding = true;
      this.button.setAttribute('aria-busy', 'true');
      const own = (window.Miscela && window.Miscela.cartHeaders) || {};
      const send = async () => {
        const seq = window.Miscela && window.Miscela.nextCartSeq ? window.Miscela.nextCartSeq() : 0;
        let response;
        let data;
        try {
          response = await fetch(`${this.dataset.cartAddUrl}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...own },
            body: JSON.stringify({
              // Built when the request leaves: the ready stock left counts the cart as the changes before it left it.
              items: chosen.map((entry) => lineItem(entry.variant)),
              sections: sections.join(','),
              sections_url: window.location.pathname,
            }),
          });
          data = await response.json();
        } catch (err) {
          throw new Error(LOST_ANSWER);
        }
        if (!response.ok) throw new Error(data.description || 'Não foi possível adicionar ao carrinho.');
        const handled = !document.dispatchEvent(
          new CustomEvent('cart:added', { cancelable: true, detail: { item: data, sections: data.sections, seq } })
        );
        if (!handled) window.location.href = this.dataset.cartUrl;
      };
      try {
        await addToCart(send);
      } catch (err) {
        error.textContent = err.message;
        error.hidden = false;
      } finally {
        this.adding = false;
        this.button.removeAttribute('aria-busy');
      }
    }
  }

  const define = (name, element) => {
    if (!customElements.get(name)) customElements.define(name, element);
  };
  define('product-form', ProductForm);
  define('product-gallery', ProductGallery);
  define('size-guide', SizeGuide);
  define('product-siblings', ProductSiblings);
  define('product-bundle', ProductBundle);
})();
